import { useContext } from 'react';
import get from 'lodash/get';
import { PapiDeviation } from '@wix/da-papi-types';
import { DeviationAttributes } from '../../ActionBar/types';
import { DeviationResourceType } from '../../types/deviation';
import { getSellableAs } from '../../types/deviation/getters';
import {
  getDeviationResourceType,
  isSimpleDeviation,
  getCanHavePremiumDownload,
  isHyped,
  isMultiImageDeviation,
  isBoostedDeviation,
  isPublished,
} from '../_helpers';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { isExtendedDeviationFullyLoaded } from '../../DeviationPage/redux/helpers';

function getIsEditable(deviation: PapiDeviation) {
  const canUserEdit: boolean = get(deviation, 'extended.canUserEdit', false);

  // Hacky way to sometimes hide the edit button
  if (
    deviation.legacyTextEditUrl === '#' ||
    (deviation.extended && deviation.extended.editUrl === '#')
  ) {
    return false;
  }

  return canUserEdit;
}

export function useDeviationAttributes(
  deviation: PapiDeviation,
  isOwner: boolean
): DeviationAttributes {
  const { isLoggedIn } = useContext(SessionContext);

  const resourceType = getDeviationResourceType(deviation);
  const isExtendedLoaded = isExtendedDeviationFullyLoaded(deviation);
  const isMultiImage = isMultiImageDeviation(deviation);
  const isSimple = isSimpleDeviation(deviation);
  const isReportable = !isOwner && !isSimple;
  const isShareable = deviation.isShareable || isSimple;
  const isGroupSubmittable =
    !isSimple &&
    (isExtendedLoaded ? !!deviation.extended?.canUserAddToGroup : isLoggedIn);
  const isDeletable = isExtendedLoaded
    ? !!deviation.extended?.canUserDelete
    : isOwner;
  const isBoostable = !!deviation.extended?.isBoostable;
  const isBoosted = isBoostedDeviation(deviation);
  const isStash = !isPublished(deviation);

  const hasMoreActions = isShareable || isReportable;
  const hasNft = false;

  return {
    hasFlag: !!deviation.dailyDeviation || isHyped(deviation) || isStash,
    hasMoreActions,
    isCommentable: !!deviation.isCommentable,
    isFavouritable: !isSimple && !isOwner,
    isGroupSubmittable,
    isDownloadable: !!deviation.isDownloadable,
    isAdoptable: !!deviation.isAdoptable,
    isPurchasable: !!deviation.isPurchasable,
    isShareable,
    isReportable,
    isSellable: getSellableAs(deviation).length > 0,
    canHavePremiumDownload: getCanHavePremiumDownload(deviation),
    isEditable: getIsEditable(deviation),
    isDeletable,
    isBackgroundEditable: !!deviation.isBackgroundEditable,
    isAuthorCommissionable: deviation.author?.type !== 'banned',
    isCoreBadgeAwardable:
      !isOwner && resourceType === DeviationResourceType.ART,
    isExtendedLoaded,
    hasNft,
    isBoostable,
    isBoosted,
    isMultiImage,
    canRequestOriginal: Boolean(
      isLoggedIn && !isOwner && deviation.extended?.originals?.isOpen
    ),
  };
}
